import publicApi from './publicApi'
import type { Attendee, GroupCheckoutResponse } from '../../pages/checkout/checkoutTypes'

export const getPublicEventTypes = async ({ access_token }: { access_token: string }) => {
  const res = await publicApi.get(`/event_types/`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  })

  return res.data
}

export const getPublicEvent = async ({ id, access_token }: { id: string; access_token: string }) => {
  const res = await publicApi.get(`/events/${id}/`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  })

  return res.data
}

export const getPublicPackages = async ({
  access_token,
  data,
}: {
  access_token: string
  data: {
    type?: string
    internal_tag?: string
  }
}) => {
  const res = await publicApi.get(`/packages/`, {
    params: data,
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  })

  return res.data
}

export const putPublicJoinEvent = async ({
  id,
  access_token,
  private_code,
  data,
}: {
  id: string
  access_token: string
  private_code: string
  data: {
    use_package: boolean
    confirmed?: boolean
    coupon_code?: string
    attendees?: Attendee[]
  }
}): Promise<GroupCheckoutResponse> => {
  const res = await publicApi.put(`/events/${id}/join/`, data, {
    params: {
      private_code: private_code,
    },
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  })

  return res.data
}

export const postPublicJoinEvent = async ({
  id,
  access_token,
  private_code,
  data,
}: {
  id: string
  access_token: string
  private_code: string
  data: {
    use_package: boolean
    confirmed?: boolean
    checkout_id?: string
  }
}) => {
  const res = await publicApi.post(`/events/${id}/join/`, data, {
    params: {
      private_code: private_code,
    },
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  })

  return res.data
}

export const getPublicClassDetails = async ({
  id,
  access_token,
  coupon_code,
}: {
  id: string
  access_token: string
  coupon_code: string | null
}) => {
  const res = await publicApi.get(`/packages/${id}/`, {
    params: { coupon_code }, // Include coupon_code in params
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  })

  return res.data
}

export const postPublicBuyPackageDiscount = async ({
  id,
  access_token,
  private_code,
  coupon_code,
}: {
  id: string
  access_token: string
  private_code: string
  coupon_code: string
}) => {
  const res = await publicApi.post(
    `/packages/${id}/`,
    {
      coupon_code: coupon_code,
    },
    {
      params: {
        private_code: private_code,
      },
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  )

  return res.data
}

export const postPublicBuyPackage = async ({
  id,
  access_token,
  private_code,
}: {
  id: string
  access_token: string
  private_code: string
}) => {
  const res = await publicApi.post(
    `/packages/${id}/`,
    {},
    {
      params: {
        private_code: private_code,
      },
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  )

  return res.data
}

export const postConfirmPayment = async ({
  access_token,
  payment_intent_id,
}: {
  access_token: string
  payment_intent_id: string
}) => {
  const res = await publicApi.post(
    `/events/confirm-payment/`,
    { payment_intent_id },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  )
  return res.data
}

export const postConfirmPackagePayment = async ({
  access_token,
  payment_intent_id,
}: {
  access_token: string
  payment_intent_id: string
}) => {
  const res = await publicApi.post(
    `/packages/confirm-payment/`,
    { payment_intent_id },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  )
  return res.data
}

/**
 * reapplyEventBookingDiscount — Plan 04-05 — Slice E (MEM-15).
 *
 * Thin axios PATCH wrapper for the server-authoritative reapply endpoint
 * (path: /events/bookings/{id}/reapply-discount/). Body is ignored server-side;
 * the server re-resolves the discount, persists applied_membership +
 * applied_discount_cents on the row, and syncs the Stripe PaymentIntent
 * (modify-or-recreate). Returns a fresh client_secret usable for
 * stripe.confirmPayment in the EventCheckout 409 flow.
 *
 * Matches the existing `postConfirmPayment` shape: takes access_token + booking_id,
 * forwards Bearer through publicApi so the call works in the same checkout
 * surface that already uses publicApi for the finalize step.
 */
export const reapplyEventBookingDiscount = async ({
  access_token,
  booking_id,
}: {
  access_token: string
  booking_id: string
}): Promise<{
  client_secret: string
  total_cents: number
  applied_discount_cents: number
  applied_membership_id: string | null
  applied_membership_name: string | null
}> => {
  const res = await publicApi.patch(
    `/events/bookings/${booking_id}/reapply-discount/`,
    {},
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    },
  )
  return res.data
}

export const postPublicInvite = async ({
  id,
  access_token,
  data,
}: {
  id: string
  access_token: string
  data: {
    member: string
  }
}) => {
  const res = await publicApi.post(`/events/${id}/invite/`, data, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  })

  return res.data
}

export const getPublicMembers = async ({
  access_token,
  data,
}: {
  access_token: string
  data: {
    search?: string
    page: number
    page_size: number
    exclude_ids?: string
  }
}) => {
  const res = await publicApi.get(`member/`, {
    params: data,
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  })

  return res.data
}
